import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  "Female Model", "Male Model", "Actor", "Actress", "Dancer",
  "Creator", "Influencer", "Commercial Talent", "Fashion Model", "Fitness Model",
];

const CITIES = ["Chandigarh", "Mohali", "Panchkula", "Delhi", "Mumbai", "Punjab", "Haryana", "Other"];

export default function Join() {
  const [f, setF] = useState({
    full_name: "", professional_name: "", category: "", city: "", phone: "", email: "",
    age: "", height: "", languages: "", skills: "", experience: "",
    instagram: "", portfolio_url: "", introduction: "", availability: "Available",
    profile_image: "", gallery: [], consent_public: true,
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF({ ...f, [k]: v });

  const upload = (key, multi = false) => async (e) => {
    const files = [...e.target.files];
    const readers = files.map((fl) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(fl);
    }));
    const urls = await Promise.all(readers);
    if (multi) set(key, [...(f[key] || []), ...urls].slice(0, 8));
    else set(key, urls[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_public) { toast.error("Consent required to submit."); return; }
    setBusy(true);
    try {
      await api.post("/join", f);
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not submit. Try again.");
    }
    setBusy(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <CheckCircle2 className="text-[#C5A059] mx-auto mb-6" size={48} />
          <div className="sv-overline mb-4">Application Received</div>
          <h1 className="font-serif-hero text-4xl lg:text-5xl text-[#F4EFE4]">Thank you.</h1>
          <p className="mt-6 text-[#CDC7BA] leading-relaxed">
            Your application has been received by SV Talent Agency. Our team will review your
            profile and reach out if selected. Approved profiles will be added to the SV Talent
            directory.
          </p>
          <div className="mt-8"><a href="/" className="sv-btn-outline">Back to Home</a></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <section className="max-w-3xl mx-auto px-4 pt-20 pb-8">
        <div className="sv-overline mb-4">Join SV Talent Agency</div>
        <h1 className="font-serif-hero text-4xl lg:text-6xl text-[#F4EFE4]">Your next opportunity<br /> starts here.</h1>
        <p className="mt-6 text-[#CDC7BA]">Submit your professional details. Approved profiles are added to the SV Talent Agency directory. Auditions, projects and selection are not guaranteed.</p>
      </section>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 pb-24">
        <div className="sv-card p-6 sm:p-10 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <input data-testid="join-name" required placeholder="Full Name *" className="sv-input" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
            <input data-testid="join-pro-name" placeholder="Professional / Display Name" className="sv-input" value={f.professional_name} onChange={(e) => set("professional_name", e.target.value)} />
            <select data-testid="join-category" required className="sv-select" value={f.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">Category *</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="join-city" required className="sv-select" value={f.city} onChange={(e) => set("city", e.target.value)}>
              <option value="">City *</option>
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="join-email" required type="email" placeholder="Email *" className="sv-input" value={f.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="join-phone" placeholder="Phone" className="sv-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            <input placeholder="Age" className="sv-input" value={f.age} onChange={(e) => set("age", e.target.value)} />
            <input placeholder="Height (optional)" className="sv-input" value={f.height} onChange={(e) => set("height", e.target.value)} />
            <input placeholder="Languages" className="sv-input" value={f.languages} onChange={(e) => set("languages", e.target.value)} />
            <input placeholder="Skills (comma separated)" className="sv-input" value={f.skills} onChange={(e) => set("skills", e.target.value)} />
            <input placeholder="Instagram Handle" className="sv-input" value={f.instagram} onChange={(e) => set("instagram", e.target.value)} />
            <input placeholder="Portfolio URL" className="sv-input" value={f.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} />
          </div>
          <textarea placeholder="Experience (previous work, credits)" rows={3} className="sv-input" value={f.experience} onChange={(e) => set("experience", e.target.value)} />
          <textarea placeholder="Short professional introduction" rows={3} className="sv-input" value={f.introduction} onChange={(e) => set("introduction", e.target.value)} />

          <div>
            <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Profile Photo</div>
            <label className="block border border-dashed border-[rgba(197,160,89,0.35)] rounded-xl p-6 text-center cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={upload("profile_image")} data-testid="join-photo" />
              {f.profile_image ? <img src={f.profile_image} className="mx-auto rounded-lg max-h-48 object-cover" alt="" /> : <div className="text-xs font-mono-label text-[#8A8478] py-6">Upload Profile Photo</div>}
            </label>
          </div>
          <div>
            <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Portfolio Gallery (up to 8)</div>
            <label className="block border border-dashed border-[rgba(197,160,89,0.35)] rounded-xl p-4 text-center cursor-pointer">
              <input type="file" accept="image/*" multiple className="hidden" onChange={upload("gallery", true)} />
              <div className="text-xs font-mono-label text-[#8A8478] py-2">Add Images</div>
              {f.gallery.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {f.gallery.map((g, i) => <img key={i} src={g} className="rounded aspect-square object-cover" alt="" />)}
                </div>
              )}
            </label>
          </div>

          <label className="flex items-start gap-3 text-sm text-[#CDC7BA] pt-2">
            <input type="checkbox" checked={f.consent_public} onChange={(e) => set("consent_public", e.target.checked)} data-testid="join-consent" className="mt-1" />
            <span>I consent to SV Talent Agency using my submitted photos and professional information on the public SV Talent directory upon approval. Private contact details will not be exposed publicly.</span>
          </label>

          <button data-testid="join-submit" disabled={busy} type="submit" className="sv-btn-primary w-full">
            {busy ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </form>
      <Footer />
    </div>
  );
}
